// pages/stock/StockHome.js
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Alert,
    Box,
    SimpleGrid,
    Skeleton,
} from '@chakra-ui/react';

// Organized imports
import { useStockInfo } from '../../hooks/useStockInfo';
import { useSecFilingsData } from '../../hooks/useSecFilingsData';
import { useReverseDcfSaves } from '../../hooks/useReverseDcfSaves';
import { useDcfSaves } from '../../hooks/useDcfSaves';
import { usePublicStockMetricCount } from '../../hooks/usePublicStockMetricCount';
import StockHomeDashboard, { buildStockHomeExternalLinks } from './StockHomeDashboard';

const sortFilingsByDateDesc = (filings) =>
    filings.toSorted((a, b) => {
        const dateA = a?.filingDateValue || Date.parse(a?.filingDate || '') || 0;
        const dateB = b?.filingDateValue || Date.parse(b?.filingDate || '') || 0;
        return dateB - dateA;
    });

const isInsiderForm = (filing) => {
    const formType = String(filing?.formTypeKey || filing?.formType || '')
        .trim()
        .toUpperCase()
        .replace(/^FORM\s+/, '');

    return (
        formType === '3' ||
        formType === '4' ||
        formType === '5' ||
        formType === '144' ||
        formType.startsWith('3/') ||
        formType.startsWith('4/') ||
        formType.startsWith('5/')
    );
};

function StockHome() {
    const { ticker } = useParams();

    // Use safe defaults to ensure hooks are always called with valid values
    const upperCaseTicker = ticker ? ticker.toUpperCase() : '';
    const lowerCaseTicker = ticker ? ticker.toLowerCase() : '';
    const { data: stockInfo, loading, error } = useStockInfo(upperCaseTicker);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const publicMetricsCount = usePublicStockMetricCount(upperCaseTicker);
    const apiUrl = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''), []);
    const secApiBaseUrl = apiUrl;
    const { filings, isFetching: filingsLoading, error: filingsError } = useSecFilingsData(upperCaseTicker, secApiBaseUrl);
    const {
        saves: reverseDcfSaves,
        loading: reverseDcfLoading,
        error: reverseDcfError,
    } = useReverseDcfSaves({
        apiUrl,
        getAccessTokenSilently,
        isAuthenticated,
        ticker: upperCaseTicker,
    });
    const {
        saves: dcfSaves,
        loading: dcfLoading,
        error: dcfError,
    } = useDcfSaves({
        apiUrl,
        getAccessTokenSilently,
        isAuthenticated,
        ticker: upperCaseTicker,
    });

    const sortedFilings = useMemo(() => sortFilingsByDateDesc(filings), [filings]);
    const latestFilings = useMemo(() => sortedFilings.slice(0, 5), [sortedFilings]);
    const insiderFilings = useMemo(
        () => sortedFilings.filter(isInsiderForm).slice(0, 3),
        [sortedFilings]
    );

    const externalLinks = useMemo(() => {
        return buildStockHomeExternalLinks({ lowerCaseTicker, stockInfo, upperCaseTicker });
    }, [lowerCaseTicker, upperCaseTicker, stockInfo]);

    const routes = useMemo(() => {
        const stockBase = `/stock/${lowerCaseTicker}`;
        return {
            analysis: `${stockBase}/analysis`,
            reverseDcf: `${stockBase}/reverse-dcf`,
            charts: `${stockBase}/charts`,
            snapshot: `${stockBase}/snapshot`,
            financials: `${stockBase}/financials/balance-sheet`,
            filings: `${stockBase}/sec-filings`,
            publicMetrics: `/stocks/${lowerCaseTicker}/metrics`,
        };
    }, [lowerCaseTicker]);

    // Handle missing ticker gracefully (after hooks are called)
    if (!ticker) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Invalid ticker symbol. Please provide a valid stock ticker.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box p={4}>
                <Skeleton height="40px" width="300px" mb={4} />
                <Skeleton height="20px" width="400px" mb={6} />
                <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} gap={4}>
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                </SimpleGrid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        {error}
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    // Handle case where stockInfo is null/undefined (shouldn't happen if error is set, but be defensive)
    if (!stockInfo) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Could not load stock information for ticker "{upperCaseTicker}". Please verify the ticker symbol is correct.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    return (
        <StockHomeDashboard
            externalLinks={externalLinks}
            filingsError={filingsError}
            filingsLoading={filingsLoading}
            insiderFilings={insiderFilings}
            isAuthenticated={isAuthenticated}
            isAuthLoading={isAuthLoading}
            latestFilings={latestFilings}
            publicMetricsCount={publicMetricsCount}
            dcfError={dcfError}
            dcfLoading={dcfLoading}
            dcfSaves={dcfSaves}
            reverseDcfError={reverseDcfError}
            reverseDcfLoading={reverseDcfLoading}
            reverseDcfSaves={reverseDcfSaves}
            routes={routes}
            stockInfo={stockInfo}
            ticker={ticker}
            upperCaseTicker={upperCaseTicker}
        />
    );
}

export default StockHome;
